import {
  Warehouse,
  InventoryItem,
  NonInventoryItem,
  InventoryWarehouseAssociationInput,
  NonInventoryWarehouseAssociationInput,
  Money,
  ProductInventory,
  SupplyItem,
  ConfigureFulfillmentWarehouseInput,
  ConfigureFulfillmentWarehouse,
  InventoryCheckBehavior,
  Orderability,
  StandingOrderBehavior,
} from '@/graphql/types/generated';
import {
  WarehouseAssociation,
  getVendorId,
  getVendorName,
  getVendorWarehousename,
} from '@/types';
import { UpsertItemForm } from '../../UpsertItem/types/UpsertItemForm';
import { priceInput, priceOutput } from '@/utils/PricingIO';

export enum WarehouseRowStatus {
  Enabled,
  EnabledForGlobalFulfillment,
  EnabledCustomBranded,
  Disabled,
  AwaitingVendor,
  AwaitingVendorAndPrice,
  AwaitingFulfillmentWarehouse,
  AwaitingVendorAndFulfillmentWarehouse,
  AwaitingFulfillmentWarehouseAndPrice,
  AwaitingPrice,
}

type WarehouseRowStatusProps = {
  warehouseId: string;
  vendorId: string;
  salePrice?: Money | null;
  fulfillmentWarehouses: ConfigureFulfillmentWarehouse[];
  isManufactured?: boolean;
};

/**
 * Whether a warehouse row may be discontinued. Vendor-backed rows are discontinuable when enabled;
 * manufactured (custom branded) rows have no procurement vendor but are still orderable, so they are
 * discontinuable once priced and assigned a fulfillment warehouse.
 */
export const isDiscontinuableStatus = (status: WarehouseRowStatus): boolean =>
  status === WarehouseRowStatus.Enabled ||
  status === WarehouseRowStatus.EnabledForGlobalFulfillment ||
  status === WarehouseRowStatus.EnabledCustomBranded;

/* Note: Updating this to reflect the UI's structure, not the GraphQL structure
 * Within the `to___WarehouseAssociationInput` functions is the data associated
 * to the appropriate GraphQL Type
 * (InventoryWarehouseAssociationInput | NonInventoryWarehouseAssociationInput)
 */
export type WarehousesRow = {
  id: string;
  name: string;
  enabled: boolean;
  hasSample: boolean;
  salePrice?: Money;
  vendorId: string;
  savedVendorId: string;
  vendorName: string;
  warehouseName: string;
  saved: boolean;
  inventory: number;
  isInventoryItem: boolean;
  sample?: SupplyItem;
  savedConfiguredFulfillmentWarehouses?: ConfigureFulfillmentWarehouse[];
  configuredFulfillmentWarehouses: ConfigureFulfillmentWarehouse[];
  savedStatus: WarehouseRowStatus;
  status: WarehouseRowStatus;
  customerFacingOutOfStock?: boolean;
  outOfStockSince?: string | null;
  orderability: Orderability;
  customOrderabilityMessage?: string | null;
  standingOrderBehavior: StandingOrderBehavior;
  // Undefined for a market the product is not yet in, so its displayed defaults always count as changed.
  savedStandingOrderBehavior?: StandingOrderBehavior;
  inventoryCheckBehavior: InventoryCheckBehavior;
  savedInventoryCheckBehavior?: InventoryCheckBehavior;
};

// Market ordering settings are sent only when edited: an omitted field leaves the stored market
// value alone, so a stale echo can never overwrite it. A new association has nothing stored to
// leave alone, and omitting the field would inherit the item-level setting instead of the value shown.
const ifChanged = <T>(value: T, saved: T | undefined) =>
  value !== saved ? value : undefined;

/**
 * Whether a market carries its own ordering settings: any market the product is sold in, including
 * custom branded ones, which have no procurement vendor and so are never `enabled`.
 */
export const hasMarketSettings = (row: WarehousesRow): boolean =>
  row.enabled || row.status === WarehouseRowStatus.EnabledCustomBranded;

export const toConfiguredFulfillmentWarehouseInput = (
  row: WarehousesRow,
): ConfigureFulfillmentWarehouseInput[] => {
  return row.configuredFulfillmentWarehouses.map(
    (fw: ConfigureFulfillmentWarehouse) => ({
      warehouseId: fw.warehouse.id,
    }),
  );
};

/**
 * Helper map function to convert a {@link WarehousesRow} to {@link InventoryWarehouseAssociationInput}
 * @param row the {@link WarehousesRow} to map from
 */
export const toInventoryWarehouseAssociationInput = (
  row: WarehousesRow,
  disablePriceEditing: boolean,
): InventoryWarehouseAssociationInput => {
  return {
    procurementVendorId: row.vendorId === '' ? null : row.vendorId,
    salePrice: disablePriceEditing
      ? typeof row.salePrice !== 'undefined'
        ? priceInput(row.salePrice)
        : null
      : priceInput(row.salePrice),
    hasSample: row.hasSample,
    warehouseId: row.id,
    orderability: row.orderability,
    // Empty is how the field is cleared, and null restores the wording the state derives.
    customOrderabilityMessage: row.customOrderabilityMessage?.trim() || null,
    standingOrderBehavior: ifChanged(
      row.standingOrderBehavior,
      row.savedStandingOrderBehavior,
    ),
    inventoryCheckBehavior: ifChanged(
      row.inventoryCheckBehavior,
      row.savedInventoryCheckBehavior,
    ),
    configuredFulfillmentWarehouses: toConfiguredFulfillmentWarehouseInput(row),
  };
};

/**
 * Helper map function to convert a {@link WarehousesRow} to {@link NonInventoryWarehouseAssociationInput}
 * @param row the {@link WarehousesRow} to map from
 */
export const toNonInventoryWarehouseAssociationInput = (
  row: WarehousesRow,
  disablePriceEditing: boolean,
): NonInventoryWarehouseAssociationInput => {
  return {
    vendorId: row.vendorId,
    salePrice: disablePriceEditing
      ? typeof row.salePrice !== 'undefined'
        ? priceInput(row.salePrice)
        : null
      : priceInput(row.salePrice),
    hasSample: row.hasSample,
    warehouseId: row.id,
    orderability: row.orderability,
    customOrderabilityMessage: row.customOrderabilityMessage?.trim() || null,
    // The out-of-stock check is inventory-only at every layer, so only standing orders go here.
    standingOrderBehavior: ifChanged(
      row.standingOrderBehavior,
      row.savedStandingOrderBehavior,
    ),
    configuredFulfillmentWarehouses: toConfiguredFulfillmentWarehouseInput(row),
  };
};

// A market the product is not yet in starts at the column defaults, so adding one can never
// silently enable either setting there.
const storedMarketItemFlags = (found?: WarehouseAssociation) => ({
  standingOrderBehavior:
    found?.standingOrderBehavior ?? StandingOrderBehavior.NotAvailable,
  inventoryCheckBehavior:
    (found?.__typename === 'InventoryWarehouseAssociation'
      ? found.inventoryCheckBehavior
      : undefined) ?? InventoryCheckBehavior.Check,
});

// Only inventory items carry manufacturedType; the cast is safe for forms/non-inventory items.
export const isManufacturedItem = (
  item: UpsertItemForm | (InventoryItem | NonInventoryItem),
): boolean => !!(item as InventoryItem)?.manufacturedType;

/**
 * Creates an array of {@link WarehousesRow}'s from a list of
 * {@link Warehouse} and a list of {@link WarehouseAssociation}
 * for use in {@link ItemForm} that matches by ID
 *
 * @param warehouses the array of ({@link Warehouse} to map from
 * @param warehouseAssociation the array of ({@link WarehouseAssociation} to match to
 */
export const createWarehouseRowsForUI = (
  warehouses: Warehouse[],
  item: UpsertItemForm | (InventoryItem | NonInventoryItem),
  productInventory?: ProductInventory[],
): WarehousesRow[] => {
  if (!warehouses) {
    return [];
  }
  const { warehouseAssociation } = item;
  const isManufactured = isManufacturedItem(item);
  // let's drill down warehouseAssociation to a definitive type

  return warehouses
    .sort((left, right) => {
      return left.name.localeCompare(right.name);
    })
    .map((warehouse) => {
      const found =
        (warehouseAssociation as WarehouseAssociation[])
          ?.filter((v) => v !== null)
          .find((v) => warehouse.id === v?.warehouse?.id) ?? undefined;

      let foundProductInventory: ProductInventory | undefined;

      if (productInventory) {
        foundProductInventory = (
          (productInventory as ProductInventory[]) || undefined
        ).find((v) => warehouse.id === v?.warehouse?.id);
      }

      const rawSalePrice = found?.salePrice ?? undefined;
      const salePrice =
        rawSalePrice === undefined ? undefined : priceOutput(rawSalePrice);

      const vendorId = found === undefined ? '' : getVendorId(found) ?? '';
      const vendorName = found === undefined ? '' : getVendorName(found) ?? '';
      const warehouseName =
        found === undefined ? '' : getVendorWarehousename(found) ?? '';

      const hasSample = found === undefined ? false : !!found.sample;

      const configuredFulfillmentWarehouses =
        found?.configuredFulfillmentWarehouses ?? [];

      // A market this product has never been associated with defaults to orderable, matching the
      // column, so adding a warehouse does not silently withhold the product there.
      const orderability = found?.orderability ?? Orderability.Orderable;
      const customOrderabilityMessage =
        found?.customOrderabilityMessage ?? null;
      const { standingOrderBehavior, inventoryCheckBehavior } =
        storedMarketItemFlags(found);

      const enabled = !!salePrice && vendorId !== '';
      const isInventoryItem: boolean =
        found?.__typename !== 'NonInventoryWarehouseAssociation';

      const status = getWarehouseRowStatus({
        warehouseId: warehouse.id,
        vendorId,
        salePrice: rawSalePrice,
        fulfillmentWarehouses: configuredFulfillmentWarehouses,
        isManufactured,
      });

      return {
        enabled,
        id: warehouse.id,
        name: warehouse.name,
        ...found,
        salePrice,
        hasSample,
        orderability,
        customOrderabilityMessage,
        standingOrderBehavior,
        savedStandingOrderBehavior: found ? standingOrderBehavior : undefined,
        inventoryCheckBehavior,
        savedInventoryCheckBehavior: found ? inventoryCheckBehavior : undefined,
        vendorId,
        vendorName,
        savedVendorId: vendorId,
        warehouseName,
        saved: vendorId !== '',
        inventory: foundProductInventory?.availableCount,
        isInventoryItem,
        savedConfiguredFulfillmentWarehouses: configuredFulfillmentWarehouses,
        configuredFulfillmentWarehouses,
        savedStatus: status,
        status,
      };
    })
    .filter((wr) => wr) as WarehousesRow[];
};

export const isGlobalFulfillmentOnlyStatus = (row: WarehousesRow): boolean => {
  return row.configuredFulfillmentWarehouses.length
    ? !fulfillmentWarehousesIncludeWarehouseId(
        row.configuredFulfillmentWarehouses,
        row.id,
      )
    : false;
};

export const getWarehouseRowStatus = ({
  warehouseId,
  vendorId,
  salePrice,
  fulfillmentWarehouses,
  isManufactured,
}: WarehouseRowStatusProps): WarehouseRowStatus => {
  const hasSalePrice = typeof salePrice !== 'undefined';
  const hasVendorId = vendorId !== '';
  const hasFulfillmentWarehouses = fulfillmentWarehouses.length > 0;
  const isWarehouseInFulfillmentList = fulfillmentWarehousesIncludeWarehouseId(
    fulfillmentWarehouses,
    warehouseId,
  );

  if (hasVendorId && hasSalePrice) {
    if (hasFulfillmentWarehouses) {
      return isWarehouseInFulfillmentList
        ? WarehouseRowStatus.Enabled
        : WarehouseRowStatus.EnabledForGlobalFulfillment;
    }
  }

  // Manufactured (custom branded) items have no procurement vendor, so they never satisfy the
  // vendor-based "Enabled" status. They are still orderable once priced and assigned a fulfillment
  // warehouse, and must show as enabled so they can be discontinued.
  if (isManufactured && hasSalePrice && hasFulfillmentWarehouses) {
    return WarehouseRowStatus.EnabledCustomBranded;
  }

  if (!hasSalePrice && !hasVendorId && !hasFulfillmentWarehouses) {
    return WarehouseRowStatus.Disabled;
  }

  // Handle partial configuration states (awaiting something)
  return determineAwaitingStatus({
    hasVendorId,
    hasSalePrice,
    hasFulfillmentWarehouses,
  });
};

const determineAwaitingStatus = ({
  hasVendorId,
  hasSalePrice,
  hasFulfillmentWarehouses,
}: {
  hasVendorId: boolean;
  hasSalePrice: boolean;
  hasFulfillmentWarehouses: boolean;
}): WarehouseRowStatus => {
  if (!hasVendorId && !hasFulfillmentWarehouses) {
    return WarehouseRowStatus.AwaitingVendorAndFulfillmentWarehouse;
  }

  if (!hasVendorId && !hasSalePrice) {
    return WarehouseRowStatus.AwaitingVendorAndPrice;
  }

  if (!hasFulfillmentWarehouses && !hasSalePrice) {
    return WarehouseRowStatus.AwaitingFulfillmentWarehouseAndPrice;
  }

  if (!hasVendorId) {
    return WarehouseRowStatus.AwaitingVendor;
  }

  if (!hasSalePrice) {
    return WarehouseRowStatus.AwaitingPrice;
  }

  if (!hasFulfillmentWarehouses) {
    return WarehouseRowStatus.AwaitingFulfillmentWarehouse;
  }

  return WarehouseRowStatus.Disabled;
};

const fulfillmentWarehousesIncludeWarehouseId = (
  fulfillmentWarehouses: ConfigureFulfillmentWarehouse[],
  warehouseId: string,
): boolean => {
  return fulfillmentWarehouses
    .map((fw: ConfigureFulfillmentWarehouse) => fw.warehouse.id)
    .includes(warehouseId);
};
